import React, { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useSystem } from '../contexts/SystemContext';
import { Loader2, AlertTriangle } from 'lucide-react';

interface PayPalButtonProps {
  amount: number;
  onSuccess: () => void;
}

const PayPalButton: React.FC<PayPalButtonProps> = ({ amount, onSuccess }) => {
  const { getPayPalClientId, loading } = useSystem();
  const [scriptError, setScriptError] = useState<string | null>(null);
  const clientId = getPayPalClientId();

  // PayPal Client IDs are typically long (80+ chars). A short string like "sandbox" or "test" will fail.
  const isValidId = clientId && clientId.length > 20;

  if (loading) {
     return <div className="text-center p-2"><Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-500" /></div>;
  }

  if (!isValidId || scriptError) {
    return (
      <div className="bg-gray-800/50 border border-gray-700/50 p-4 rounded-xl text-center flex flex-col items-center gap-2">
        <AlertTriangle className="w-6 h-6 text-yellow-500/50" />
        <div>
          <p className="text-gray-300 text-sm font-medium">Payment System Unavailable</p>
          <p className="text-gray-500 text-[10px]">
            {scriptError ? "Connection failed" : "Configuration pending by admin"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full z-0 relative">
      <PayPalScriptProvider 
        options={{ "clientId": clientId!, currency: "USD" }}
      >
        <PayPalButtons 
          style={{ layout: "horizontal", height: 48, tagline: false, shape: "rect", color: "blue" }}
          createOrder={(data, actions) => {
            return actions.order.create({
              intent: "CAPTURE",
              purchase_units: [
                {
                  amount: {
                    currency_code: "USD",
                    value: amount.toString(),
                  },
                },
              ],
            });
          }}
          onApprove={(data, actions) => {
             if (!actions.order) return Promise.resolve();
             return actions.order.capture().then((details) => {
                onSuccess();
             });
          }}
          onError={(err) => {
            console.error("PayPal Transaction Error:", err);
            alert("Payment failed. Please try again or contact support.");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalButton;